
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {Getter} from "vuex-class";
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";
import { SCOPED_GROUP_PREFIX } from "@/utils/constants";

const ITEMS_PER_PAGE_INCREASED: number = 25;

@Component({
    components: {
        MylobyAutocomplete,
    },
})
export default class KeyGroupCreation extends Vue {

    get hasDifferentBuildingIds(): boolean {
        const allItems = [...this.selectedNonTransferables, ...this.selectedKeys];
        if (allItems.length) {
                const firstId = allItems[0].buildingId;
                return allItems.some((item) => !item.buildingId || item.buildingId !== firstId);
            } else {
                return false;
            }
    }
    @Prop({ default: () => [] })
    public preSelectedKeys: any[];

    @Prop({ default: false })
    public displayCreateKeyGroupDialog: boolean;

    @Prop({ default: null })
    public tab: string;

    @Getter private currentUser: any;
    @Getter private envCompanyId: number|null;
    @Getter private isMobile: boolean;
    @Getter private canDoInEnvCompany: (right: string, companyId?: number) => boolean;

    private keyGroupCreation: any = {};
    private loadingKeyGroupCreation: boolean = false;
    private targetKeys: string = "myKeys";
    // Keys
    private keys = [];
    private selectedKeys = [];
    private loadingKeys: boolean = false;
    private keySearch: string = null;
    private keysPagination: any = {
        search: "",
        page: 1,
        itemsPerPage: 10,
        tab: null,
    };
    // NonTransferables
    private nonTransferables = [];
    private selectedNonTransferables = [];
    private loadingNonTransferables: boolean = false;
    private nonTransferablesSearch: string = null;
    private nonTransferablesPagination: any = {
      search: "",
      page: 1,
      itemsPerPage: 10,
    };
    private SCOPED_GROUP_PREFIX = SCOPED_GROUP_PREFIX;

    public mounted() {
        this.init();
    }

    @Watch("keySearch", { immediate: false})
    public keySearchHandler() {
        this.keysPagination.search = this.keySearch;
        this.getKeysList();
    }

    @Watch("nonTransferablesSearch", { immediate: true})
    public nonTransferablesSearchHandler() {
        this.nonTransferablesPagination.search = this.nonTransferablesSearch;
        this.getNonTransferablesList();
    }

    @Watch("targetKeys", { immediate: true})
    public targetKeysHandler() {
        this.keys = [];
        this.keysPagination.tab = this.targetKeys;
        this.getKeysList();
    }

    @Watch("displayCreateKeyGroupDialog", { immediate: false})
    public displayCreateKeyGroupDialogHandler(val: boolean) {
        if (val) {
          this.init();
        }
    }
    @Watch("keyGroupCreation.buildingId", { immediate: false})
    public async buildingIdHandler() {
        await Promise.all([
        this.getKeysList(),
        this.getNonTransferablesList(),
        ]);
        this.selectedKeys = this.keyGroupCreation.buildingId ? this.keys : [];
        this.selectedNonTransferables = this.keyGroupCreation.buildingId ? this.nonTransferables : [];
    }

    private init(): void {
        if (this.preSelectedKeys?.length) {
            this.selectedKeys = this.preSelectedKeys;
        }
        if (this.tab) {
            this.keysPagination.tab = this.tab;
        } else {
            this.targetKeys = this.currentUser.companyId ? "myKeys" : "sharedKeys";
        }
    }

    private async getKeysList(): Promise<void> {
        this.loadingKeys = true;
        await this.$store.dispatch("users/getMyKeys", {
            id: this.currentUser.id,
            query: {
              role: this.keysPagination?.tab === "myKeys" ? "MANAGER" : "GUEST",
              search: this.keysPagination.search,
              page: this.keysPagination.page,
              itemsPerPage: this.keyGroupCreation.buildingId ?
              ITEMS_PER_PAGE_INCREASED : this.keysPagination.itemsPerPage,
              buildingId: this.keyGroupCreation.buildingId,
            },
        }).then((res) => {
            this.keys = res.keys.map((key: any) => {
                return { text: key.name, value: key, ...key };
            });
        }).finally(() => {
          this.loadingKeys = false;
        });
    }

    private async getNonTransferablesList(): Promise<void> {
        this.loadingNonTransferables = true;
        await this.$store.dispatch("companies/getNonTransferables", {
            id: this.envCompanyId,
            query: {
              search: this.nonTransferablesPagination.search,
              page: this.nonTransferablesPagination.page,
              itemsPerPage: this.keyGroupCreation.buildingId ?
              ITEMS_PER_PAGE_INCREASED : this.nonTransferablesPagination.itemsPerPage,
              buildingId: this.keyGroupCreation.buildingId,
            },
        }).then((res) => {
            this.nonTransferables = res.rows.map((nT: any) => {
                return { text: nT.name, value: nT, ...nT };
            });
        }).finally(() => {
          this.loadingNonTransferables = false;
        });
    }

    private createKeyGroup() {
        this.loadingKeyGroupCreation = true;
        this.keyGroupCreation.companyId = this.envCompanyId;
        if (this.keyGroupCreation.scoped) {
          this.selectedKeys = [];
          this.selectedNonTransferables = [];
          this.targetKeys = "myKeys";
        }
        this.$store.dispatch("keyGroups/create", {
            keyIds: this.selectedKeys.map((k: any) => k.id),
            nonTransferableIds: this.selectedNonTransferables.map((nT: any) => nT.id),
            keyGroupData: {
              ...this.keyGroupCreation,
              target: this.keysPagination.tab,
              name: (this.keyGroupCreation.scoped ? SCOPED_GROUP_PREFIX : "") + this.keyGroupCreation.name,
            },
        })
        .then((res) => {
            this.$store.commit("alerts/displaySuccess", {
              icon: "icon-picto_cle-partagees",
              msg: this.$i18n?.t("keyGroup.creation.success"),
            });
            this.reset();
            this.$emit("reset");
            this.$emit("close");
        })
        .catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
        })
        .finally(() => {
          this.loadingKeyGroupCreation = false;
        });
    }

    private reset() {
        this.keys = [];
        this.keyGroupCreation = {};
        this.selectedKeys = [];
        this.keySearch = null;
    }
}
